/**
 * MapViewQ2Incidents — cuadrante 2 del MapView en modo split.
 *
 * Lista compacta de incidentes activos del sitio con clip 6s y acciones
 * rápidas (decisión Paulo 2026-06-11: Q2 = incidentes accionables).
 *
 * Cada fila muestra:
 *   - severidad con color (low/medium/high/critical)
 *   - hora relativa (10:19 → "hace 12 min")
 *   - tipo de evento + cámara/ubicación
 *   - botones rápidos: Ver clip, Asignar, Falso positivo
 *
 * Click en una fila dispara onSelectIncident — el padre MapView lo conecta
 * para abrir el IncidentDetailPanel completo en el SlideDrawer.
 *
 * Recibe incidents como prop controlada por el padre (el WS broadcast
 * incident_created los va sumando en vivo).
 */

import { useMemo } from 'react'


const SEV_BG = {
  low:      { bg: '#1f2937', text: '#9ca3af', label: 'Bajo'    },
  medium:   { bg: '#78350f', text: '#fcd34d', label: 'Medio'   },
  high:     { bg: '#7f1d1d', text: '#fca5a5', label: 'Alto'    },
  critical: { bg: '#581c87', text: '#d8b4fe', label: 'Crítico' },
}

const EVENT_ICONS = {
  weapon_detected   : '🔫',
  loitering_detected: '⏱️',
  loitering         : '⏱️',
  line_crossing     : '🚧',
  face_match        : '👤',
  vehicle_detected  : '🚗',
  person_detected   : '🚶',
  person_alert      : '🚶',
  intrusion         : '⚠️',
  lpr               : '🪪',
  incident          : '⚠️',
}


export default function MapViewQ2Incidents({
  site,
  incidents = [],            // [{ id, severity, status, type, camera_name, created_at, ... }]
  onSelectIncident,
  onMarkFalsePositive,       // (incidentId) => void
}) {
  // Solo activos (no resueltos / cerrados)
  const active = useMemo(() => {
    return incidents
      .filter(i => i.status !== 'resolved' && i.status !== 'closed')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  }, [incidents])

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      width: '100%', height: '100%',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '8px 12px',
        background: 'var(--bg-elev-1)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
          Incidentes activos
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {active.length === 0 ? 'sin alertas' : `${active.length} ${active.length === 1 ? 'incidente' : 'incidentes'}`}
        </div>
      </div>

      {/* Lista */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {active.length === 0 && (
          <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            height: '100%', padding: 24,
            color: 'var(--text-muted)', fontSize: 12, gap: 6,
          }}>
            <div style={{ fontSize: 28, opacity: 0.6 }}>✓</div>
            <div>Sin incidentes activos en este sitio</div>
          </div>
        )}

        {active.map(inc => {
          const sev = SEV_BG[inc.severity] || SEV_BG.low
          const icon = EVENT_ICONS[inc.type] || '●'
          return (
            <div
              key={inc.id}
              onClick={() => onSelectIncident && onSelectIncident(inc)}
              style={{
                padding: '10px 12px',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 4,
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,145,255,0.06)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {/* Línea superior: severidad + hora + tipo */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center',
                  padding: '1px 7px', fontSize: 9, fontWeight: 700,
                  background: sev.bg, color: sev.text,
                  borderRadius: 3, letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  flexShrink: 0,
                }}>{sev.label}</span>
                <span style={{ fontSize: 14, flexShrink: 0 }}>{icon}</span>
                <span style={{
                  fontSize: 12, fontWeight: 500,
                  color: 'var(--text-primary)',
                  flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {labelOfEvent(inc.type)}
                </span>
                <span style={{
                  fontSize: 10, color: 'var(--text-muted)',
                  flexShrink: 0,
                }}>
                  {timeAgo(inc.created_at)}
                </span>
              </div>

              {/* Línea inferior: cámara/ubicación + acciones rápidas */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 22 }}>
                <span style={{
                  fontSize: 11, color: 'var(--text-muted)',
                  flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {inc.camera_name || inc.location || '—'}
                </span>
                {onMarkFalsePositive && (
                  <button
                    onClick={e => {
                      e.stopPropagation()
                      onMarkFalsePositive(inc.id)
                    }}
                    title="Marcar como falso positivo"
                    style={{
                      padding: '2px 7px', fontSize: 10,
                      background: 'transparent',
                      border: '1px solid var(--border)',
                      borderRadius: 3,
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                    }}
                  >
                    FP
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}


// ─── Helpers ──────────────────────────────────────────────────────────────

function labelOfEvent(type) {
  const m = {
    weapon_detected   : 'Arma detectada',
    loitering_detected: 'Merodeo detectado',
    loitering         : 'Merodeo',
    line_crossing     : 'Cruce de perímetro',
    face_match        : 'Match facial',
    vehicle_detected  : 'Vehículo detectado',
    person_detected   : 'Persona detectada',
    person_alert      : 'Alerta de persona',
    intrusion         : 'Intrusión',
    lpr               : 'Lectura de patente',
    incident          : 'Incidente',
  }
  return m[type] || type
}

function timeAgo(iso) {
  if (!iso) return ''
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (sec < 60)    return `hace ${sec}s`
  if (sec < 3600)  return `hace ${Math.floor(sec / 60)} min`
  if (sec < 86400) return `hace ${Math.floor(sec / 3600)} h`
  return new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
}
